import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from '@chakra-ui/react';
import Card from 'components/card';
import SearchIcon from 'components/icons/SearchIcon';
import { MdClose, MdAdd, MdDelete } from 'react-icons/md';
import { getProductsByIds, getProduct, searchProductsForSelect, createProduct } from 'interfaces/products';
import { getSupplier, getSupplierProducts, addProductToSupplier } from 'interfaces/supplier';
import { toast } from 'react-toastify';

const CreatePurchaseModal = (props: {
  isOpen: boolean;
  onClose: () => void;
  suppliers: any[];
  onCreated: (purchase: any) => void;
  createPurchase: (payload: any) => Promise<any>;
}) => {
  const { isOpen, onClose, suppliers, onCreated, createPurchase } = props;

  const pendingAdds = useRef<Set<string>>(new Set());
  const [supplierId, setSupplierId] = useState('');
  const [noSupplier, setNoSupplier] = useState(false);
  const [supplierProducts, setSupplierProducts] = useState<any[]>([]);
  const [catalogProducts, setCatalogProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState<
    { productId: string;
      name: string;
      qty: number;
      stock: number;
      alert: number;
      unitPrice: number;
      unitPriceSource: 'last' | 'product' | 'none';
      vatPurchase?: number;
    }[]
  >([]);
  const [submitting, setSubmitting] = useState(false);
  const [eta, setEta] = useState<string>('');
  const [minEta, setMinEta] = useState<string>('');
  const [etaError, setEtaError] = useState<string>('');
  const [deliveryFees, setDeliveryFees] = useState<number>(0);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [showSupplierList, setShowSupplierList] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [quickProductName, setQuickProductName] = useState('');
  const [quickProductUnity, setQuickProductUnity] = useState('Kg');
  const [quickProductPrice, setQuickProductPrice] = useState<string>('');
  const [quickProductVatPurchase, setQuickProductVatPurchase] = useState<string>('');
  const [quickProductVatSell, setQuickProductVatSell] = useState<string>('');
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [duplicateSuggestions, setDuplicateSuggestions] = useState<any[]>([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [quickProductSupplierIds, setQuickProductSupplierIds] = useState<string[]>([]);
  const [showQuickSupplierPicker, setShowQuickSupplierPicker] = useState(false);

  const labelSupplier = (s: any) =>
   s?.displayName || s?.company || s?.name || 'Fournisseur';
  const filteredSuppliers = useMemo(() => {
   if (!supplierSearch) return suppliers;
   const q = supplierSearch.toLowerCase();
   return suppliers.filter((s: any) =>
     labelSupplier(s).toLowerCase().includes(q),
   );
 }, [supplierSearch, suppliers]);

  const formatYMD = (d: Date) =>
    d.toISOString().split('T')[0];

  const addDays = (d: Date, days: number) => {
    const nd = new Date(d);
    nd.setDate(nd.getDate() + days);
    return nd;
  };

  const labelSource = (s: 'last' | 'product' | 'none') =>
    s === 'last'
      ? 'dernier achat'
      : s === 'product'
        ? "prix d'achat produit"
        : '—';

  const formatCurrency = (v: number) =>
    Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v || 0);

  const isInAlert = (p: any) => {
    const q = Number(p?.quantity) || 0;
    const a = Number(p?.alert);
    return a > 0 && q <= a;
  };

  useEffect(() => {
    if (!isOpen) {
      setSupplierId('');
      setNoSupplier(false);
      setSupplierProducts([]);
      setCatalogProducts([]);
      setItems([]);
      setSearch('');
      setEta('');
      setMinEta('');
      setEtaError('');
      setDeliveryFees(0);
      resetQuickCreateForm();
    }
  }, [isOpen]);

  // Recherche de doublons potentiels pendant la saisie du nom (catalogue entier,
  // pas seulement les produits déjà liés au fournisseur courant).
  useEffect(() => {
    if (!showQuickCreate) {
      setDuplicateSuggestions([]);
      return;
    }
    const q = quickProductName.trim();
    if (q.length < 2) {
      setDuplicateSuggestions([]);
      setCheckingDuplicates(false);
      return;
    }
    let cancelled = false;
    setCheckingDuplicates(true);
    const timer = setTimeout(async () => {
      const results = await searchProductsForSelect(q);
      if (!cancelled) {
        setDuplicateSuggestions(results.slice(0, 5));
        setCheckingDuplicates(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [showQuickCreate, quickProductName]);

  // Recherche dans tout le catalogue (mode sans fournisseur)
  useEffect(() => {
    if (!noSupplier) return;
    if (!search.trim()) {
      setCatalogProducts([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingProducts(true);
      const results = await searchProductsForSelect(search);
      if (!cancelled) {
        setCatalogProducts(results);
        setLoadingProducts(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [noSupplier, search]);

  useEffect(() => {
    const load = async () => {
      if (!supplierId) return;
      setLoadingProducts(true);

      const supplier = await getSupplier(supplierId);
      const fees = Number(supplier?.deliveryFees) || 0;
      const deliveryTime = Number(supplier?.deliveryTime) || 0;
      const today = new Date();
      const theoretical = addDays(today, deliveryTime);
      const theoreticalYMD = formatYMD(theoretical);
      setMinEta(theoreticalYMD);
      setDeliveryFees(fees);
      setEta((prev) => {
        if (!prev) return theoreticalYMD;
        return prev < theoreticalYMD ? theoreticalYMD : prev;
      });
      setEtaError('');

      let productIds: string[] =
        Array.isArray(supplier?.products)
          ? supplier.products
          : Array.isArray(supplier?.productIds)
          ? supplier.productIds
          : [];
 if (productIds.length === 0) {
    const legacy = await getSupplierProducts(supplierId);
    if (Array.isArray(legacy) && legacy.length > 0) {
      if (legacy[0] && typeof legacy[0] === 'object' && legacy[0].name) {
        setSupplierProducts(legacy);
        setLoadingProducts(false);
        return;
      }
      if (legacy.every((v: any) => typeof v === 'string')) {
        productIds = legacy;
      }
    }
  }

  if (productIds.length === 0) {
    setSupplierProducts([]);
    setLoadingProducts(false);
    return;
  }
 const products = await getProductsByIds(productIds);
 products.sort((a: any, b: any) =>
   (a?.name || '').localeCompare(b?.name || ''),
 );
 setSupplierProducts(products);
 setLoadingProducts(false);
};
load();
}, [supplierId]);

const validateEta = () => {
    if (!eta) {
      setEtaError('Date de réception requise');
      return false;
    }
    if (minEta && eta < minEta) {
      setEtaError(
        `La date doit être ≥ ${minEta.split('-').reverse().join('/')}`,
      );
      return false;
    }
    setEtaError('');
    return true;
  };
  
const filteredProducts = useMemo(() => {
    // Mode sans fournisseur: la recherche est déjà faite côté serveur
    if (noSupplier) return catalogProducts;
    if (!search) return supplierProducts;
    const s = search.toLowerCase();
    return supplierProducts.filter(
      (p: any) =>
        p.name?.toLowerCase().includes(s) ||
        p.ref?.toLowerCase().includes(s) ||
        p.code?.toLowerCase().includes(s)
    );
  }, [noSupplier, catalogProducts, supplierProducts, search]);

  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];
    list.sort((a: any, b: any) => {
      const aAlert = isInAlert(a);
      const bAlert = isInAlert(b);
      if (aAlert !== bAlert) return aAlert ? -1 : 1;
      return (a?.name || '').localeCompare(b?.name || '');
    });
    return list;
}, [filteredProducts]);

const getVatFromProduct = (p: any) => {
    const raw = p?.vatPurchase ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
};

  const alreadyPicked = new Set(items.map((i) => i.productId));

  const addLine = async (p: any) => {
    if (alreadyPicked.has(p._id) || pendingAdds.current.has(p._id)) return;
    pendingAdds.current.add(p._id);
    try {
      const basePrice = Number(p.purchasePrice ?? 0) || 0;
      const baseSource: 'last' | 'product' | 'none' =
        basePrice > 0 ? 'product' : 'none';

      let vat = getVatFromProduct(p);
      try {
        const full = await getProduct(p._id);
        if (full) vat = getVatFromProduct(full);
      } catch (_) {}

      setItems((prev) => {
        // Double-vérification dans l'updater pour couvrir les mises à jour concurrentes
        if (prev.some((l) => l.productId === p._id)) return prev;
        return [
          ...prev,
          {
            productId: p._id,
            name: p.name || 'Produit',
            qty: 1,
            stock: Number(p.quantity) || 0,
            alert: p.alert,
            unitPrice: basePrice,
            unitPriceSource: baseSource,
            vatPurchase: vat,
          },
        ];
      });
    } finally {
      pendingAdds.current.delete(p._id);
    }
  };

  const generateQuickRef = (name: string) => {
    const words = name.trim().split(/\s+/);
    let prefix = '';
    for (const word of words) {
      if (word.length > 0 && prefix.length < 2) prefix += word[0];
      if (prefix.length >= 2) break;
    }
    const suffix = Date.now().toString(36).toUpperCase().slice(-5);
    return `${(prefix || 'TL').toUpperCase()}-${suffix}`;
  };

  const resetQuickCreateForm = () => {
    setShowQuickCreate(false);
    setQuickProductName('');
    setQuickProductUnity('Kg');
    setQuickProductPrice('');
    setQuickProductVatPurchase('');
    setQuickProductVatSell('');
    setDuplicateSuggestions([]);
    setQuickProductSupplierIds([]);
    setShowQuickSupplierPicker(false);
  };

  const toggleQuickProductSupplier = (id: string) => {
    setQuickProductSupplierIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  // Lie un produit (nouveau ou existant) à chaque fournisseur sélectionné dans
  // le mini-formulaire. addProductToSupplier utilise $addToSet côté back donc
  // rappeler pour un lien déjà existant est sans effet (idempotent).
  const linkProductToSuppliers = async (product: any, supplierIds: string[]) => {
    if (supplierIds.length === 0) return;
    const results = await Promise.allSettled(
      supplierIds.map((id) => addProductToSupplier(id, product._id)),
    );
    if (results.some((r) => r.status === 'rejected')) {
      toast.warn('Produit lié mais association fournisseur partielle.');
    }
    if (supplierId && !noSupplier && supplierIds.includes(supplierId)) {
      setSupplierProducts((prev) =>
        prev.some((sp: any) => sp._id === product._id) ? prev : [...prev, product],
      );
    }
  };

  // Ajoute un produit existant remonté par la détection de doublon, au lieu
  // d'en créer un nouveau.
  const pickExistingProduct = async (p: any) => {
    await linkProductToSuppliers(p, quickProductSupplierIds);
    await addLine(p);
    resetQuickCreateForm();
  };

  const handleQuickCreateProduct = async () => {
    const name = quickProductName.trim();
    if (!name || creatingProduct) return;
    setCreatingProduct(true);
    const purchasePrice = Number(quickProductPrice) || 0;
    const vatPurchase = Number(quickProductVatPurchase) || 0;
    const vatSell = Number(quickProductVatSell) || vatPurchase;
    const supplierIdsToLink = quickProductSupplierIds;
    const payload = {
      name,
      type: 'brut',
      ref: generateQuickRef(name),
      unity: quickProductUnity.trim() || 'Kg',
      quantity: 0,
      price: purchasePrice,
      vat: vatSell,
      purchasePrice,
      vatPurchase,
      hiddenFromShop: true,
      // Garde Product.suppliers en phase avec Supplier.products (addProductToSupplier
      // ci-dessous ne met à jour que le second, cf. bug d'asymétrie constaté).
      suppliers: supplierIdsToLink,
    };
    const created = await createProduct(payload);
    if (!created || !created._id) {
      toast.error('Erreur lors de la création du produit');
      setCreatingProduct(false);
      return;
    }
    await linkProductToSuppliers(created, supplierIdsToLink);
    await addLine(created);
    toast.success('Produit créé et ajouté au bon de commande.');
    resetQuickCreateForm();
    setCreatingProduct(false);
  };

    const totalArticlesHT = useMemo(
           () =>
             items.reduce(
               (sum, l) => sum + (Number(l.unitPrice) || 0) * (Number(l.qty) || 0),
               0,
             ),
           [items],
         );
         const totalTVA = useMemo(
           () =>
             items.reduce((sum, l) => {
               const ht = (Number(l.unitPrice) || 0) * (Number(l.qty) || 0);
               const rate = Number(l.vatPurchase ?? 0) / 100;
               return sum + ht * (rate > 0 ? rate : 0);
             }, 0),
           [items],
         );
         const totalHT = useMemo(
               () => (totalArticlesHT || 0) + (deliveryFees || 0),
               [totalArticlesHT, deliveryFees],
             );
             const totalTTC = useMemo(
               () => totalHT + totalTVA,
               [totalHT, totalTVA],
             );


  const updateQty = (productId: string, qty: number) => {
    if (qty < 0) qty = 0;
    setItems((prev) =>
      prev.map((l) => (l.productId === productId ? { ...l, qty } : l))
    );
  };

  const removeLine = (productId: string) => {
    setItems((prev) => prev.filter((l) => l.productId !== productId));
  };

  const canSubmit = (supplierId || noSupplier) && items.length > 0 && items.every((l) => l.qty > 0) && eta && !etaError;

  const generatePurchaseId = () => {
    const ts = Date.now().toString(36).toUpperCase();
    const rand = Math.random().toString(36).substring(2, 8).toUpperCase(); 
    return `PO-${ts}${rand}`.substring(0, 8); 
  };

  const handleSubmit = async (withLink: boolean) => {
    if (!validateEta()) return;
    if (!canSubmit) return;
    setSubmitting(true);
    const now = new Date();
    const purchaseId = generatePurchaseId();
    const createdBy =
      localStorage.getItem('userId') ||
      localStorage.getItem('user') ||
      'unknown';
    const itemsWithVat = await Promise.all(
     items.map(async (l) => {
       let vat = l.vatPurchase;
       if (vat === undefined || vat === null) {
         try {
           const full = await getProduct(l.productId);
           vat = getVatFromProduct(full);
         } catch (_) {
           vat = 0;
         }
       }
       return {
         productId: l.productId,
         name: l.name,
         quantity: l.qty,
         price: Number(l.unitPrice) || 0, // HT
         vat: Number(vat) || 0,
       };
     })
   );
   const totalHTValue = Number(totalHT.toFixed(2)) || 0;
   const totalTTCValue = Number(totalTTC.toFixed(2)) || 0;
    const payload = {
      ...(noSupplier ? {} : { supplierId, supplier: supplierId }),
      purchaseId,
      purchaseDate: now.toISOString(),
      status: 'pending',
      deliveryPurchase: eta ? new Date(eta).toISOString() : undefined,
      items: itemsWithVat,
      totalHT: totalHTValue,
      totalPrice: totalTTCValue,
      deliveryFees: Number(deliveryFees.toFixed(2)) || 0,
      pay: false,
      createdBy,
    };
    const created = await createPurchase(payload);
    setSubmitting(false);
    if (created) {
      onCreated(created);
      window.open(
        `/view-purchase/${created.magicLinkUuid}`,
        '_blank',);
      onClose();

    } else {
      alert('Erreur lors de la création du bon de commande');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="ml">
      <ModalOverlay className="bg-[#000] !opacity-50 backdrop-blur-sm" />
      <ModalContent className="md:top-[5vh] !overflow-hidden !z-[1002]">
        <ModalBody className="p-0">
          <Card extra="p-6 max-h-[84vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-lg font-bold">Créer un bon de commande</h1>
                <p className="text-xs text-gray-600 mt-1">
                  {noSupplier
                    ? 'Ajoutez des produits et des quantités.'
                    : 'Sélectionnez un fournisseur, ajoutez des produits et des quantités.'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <MdClose className="w-5 h-5" />
              </button>
            </div>

            {/* Étape fournisseur */}
            <div className="mb-5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-gray-600">
                  Fournisseur
                </label>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={noSupplier}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setNoSupplier(checked);
                      setSupplierId('');
                      setSupplierSearch('');
                      setShowSupplierList(false);
                      setItems([]); // reset lignes si changement de mode
                      setMinEta('');
                      setDeliveryFees(0);
                      if (checked) setEtaError('');
                    }}
                  />
                  Commande sans fournisseur
                </label>
              </div>
              {!noSupplier && (
              <div className="mt-1 relative">
               <input
                 type="text"
                 value={
                   supplierId
                     ? labelSupplier(
                         suppliers.find(
                           (s: any) =>
                             (s._id || s.id) === supplierId,
                         ),
                       )
                     : supplierSearch
                 }
                 onChange={(e) => {
                   setSupplierId('');
                   setSupplierSearch(e.target.value);
                   setShowSupplierList(true);
                 }}
                 onFocus={() => setShowSupplierList(true)}
                 onBlur={() => setTimeout(() => setShowSupplierList(false), 200)}
                 placeholder="Rechercher un fournisseur…"
                 className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-toola-500"
               />
               {showSupplierList && (
                 <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border bg-white shadow-md">
                   {filteredSuppliers.length === 0 && (
                     <div className="px-3 py-2 text-xs text-gray-500">
                       Aucun résultat
                     </div>
                   )}
                   {filteredSuppliers.map((s: any) => {
                     const id = s._id || s.id;
                     return (
                       <button
                         key={id}
                         type="button"
                         onClick={() => {
                           setSupplierId(id);
                           setSupplierSearch('');
                           setShowSupplierList(false);
                           setItems([]); // reset lignes si changement
                         }}
                         className={`flex w-full items-start px-3 py-2 text-left text-xs hover:bg-lightPrimary ${
                           supplierId === id ? 'bg-lightPrimary' : ''
                         }`}
                       >
                         <span className="truncate">
                           {labelSupplier(s)}
                           {s?.deliveryFees ? (
                             <span className="ml-2 text-[10px] text-gray-500">
                               Frais: {formatCurrency(Number(s.deliveryFees))}
                         </span>
                           ) : null}
                         </span>
                       </button>
                     );
                })}
             </div>
           )}
         </div>
              )}
            </div>
            <div className="mb-5 -mt-3">
              {(supplierId || noSupplier) && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600">
                      Date de réception (prévisionnelle)
                    </label>
                    <input
                      type="date"
                      value={eta}
                      min={minEta || undefined}
                      onChange={(e) => {
                        setEta(e.target.value);
                        // validation immédiate
                        if (minEta && e.target.value < minEta) {
                          setEtaError(
                            `La date doit être ≥ ${minEta
                              .split('-')
                              .reverse()
                              .join('/')}`,
                          );
                        } else {
                            setEtaError('');
                        }
                      }}
                      className={`mt-1 w-full rounded-xl border px-3 py-2 text-sm outline-none ${
                        etaError
                          ? 'border-red-400 focus:border-red-500'
                          : 'border-gray-300 focus:border-toola-500'
                      }`}
                    />
                    <p className="mt-1 text-[10px] text-gray-500">
                      {minEta
                        ? `Délai théorique min: ${minEta
                            .split('-')
                            .reverse()
                            .join('/')}`
                        : '—'}
                    </p>
                    {etaError && (
                      <p className="text-[11px] text-red-500 mt-1">
                        {etaError}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Produits du fournisseur / catalogue complet */}
            {(supplierId || noSupplier) && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-semibold">
                    {noSupplier ? 'Tous les produits' : 'Produits du fournisseur'}
                  </h2>
                  <div className="flex items-center gap-2">
                    <div className="flex h-[32px] items-center rounded-xl bg-lightPrimary text-sm text-gray-600 w-64">
                      <SearchIcon />
                      <input
                        type="text"
                        placeholder="Rechercher…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="block w-full rounded-full bg-lightPrimary text-xs text-navy-700 outline-none"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setShowQuickCreate((v) => {
                          const next = !v;
                          if (next) {
                            setQuickProductSupplierIds(
                              supplierId && !noSupplier ? [supplierId] : [],
                            );
                          }
                          return next;
                        })
                      }
                      className="flex h-[32px] items-center gap-1 whitespace-nowrap rounded-xl border border-toola-500 px-2.5 text-xs font-medium text-toola-500 hover:bg-toola-50"
                    >
                      <MdAdd className="w-4 h-4" />
                      Nouveau produit
                    </button>
                  </div>
                </div>

                {showQuickCreate && (
                  <div className="mb-3 rounded-xl border border-toola-200 bg-toola-50/40 p-3">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                      <div className="col-span-2 md:col-span-1">
                        <label className="text-[10px] font-medium text-gray-600">
                          Nom*
                        </label>
                        <input
                          type="text"
                          autoFocus
                          value={quickProductName}
                          onChange={(e) => setQuickProductName(e.target.value)}
                          placeholder="Ex: Pommes Gala"
                          className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs outline-none focus:border-toola-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-gray-600">
                          Unité
                        </label>
                        <input
                          type="text"
                          value={quickProductUnity}
                          onChange={(e) => setQuickProductUnity(e.target.value)}
                          placeholder="Kg"
                          className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs outline-none focus:border-toola-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-gray-600">
                          Prix d'achat HT
                        </label>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={quickProductPrice}
                          onChange={(e) => setQuickProductPrice(e.target.value)}
                          placeholder="0.00"
                          className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs outline-none focus:border-toola-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-gray-600">
                          TVA achat (%)
                        </label>
                        <input
                          type="number"
                          min={0}
                          step="0.1"
                          value={quickProductVatPurchase}
                          onChange={(e) => setQuickProductVatPurchase(e.target.value)}
                          placeholder="5.5"
                          className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs outline-none focus:border-toola-500"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-medium text-gray-600">
                          TVA vente (%)
                        </label>
                        <input
                          type="number"
                          min={0}
                          step="0.1"
                          value={quickProductVatSell}
                          onChange={(e) => setQuickProductVatSell(e.target.value)}
                          placeholder={quickProductVatPurchase || '5.5'}
                          className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs outline-none focus:border-toola-500"
                        />
                      </div>
                    </div>

                    <div className="mt-2">
                      <label className="text-[10px] font-medium text-gray-600">
                        Fournisseur(s) liés
                      </label>
                      <div className="relative mt-1">
                        <button
                          type="button"
                          onClick={() => setShowQuickSupplierPicker((v) => !v)}
                          className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-left text-xs text-gray-700 hover:border-toola-500"
                        >
                          <span className="truncate">
                            {quickProductSupplierIds.length === 0
                              ? 'Aucun fournisseur sélectionné'
                              : quickProductSupplierIds
                                  .map((id) =>
                                    labelSupplier(
                                      suppliers.find(
                                        (s: any) => (s._id || s.id) === id,
                                      ),
                                    ),
                                  )
                                  .join(', ')}
                          </span>
                          <span className="text-[10px] text-gray-400">
                            {showQuickSupplierPicker ? '▲' : '▼'}
                          </span>
                        </button>
                        {showQuickSupplierPicker && (
                          <div className="absolute z-30 mt-1 max-h-40 w-full overflow-y-auto rounded-lg border bg-white p-1.5 shadow-lg">
                            {suppliers.length === 0 && (
                              <p className="px-2 py-1 text-[11px] text-gray-500">
                                Aucun fournisseur disponible
                              </p>
                            )}
                            {suppliers.map((s: any) => {
                              const id = s._id || s.id;
                              return (
                                <label
                                  key={id}
                                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-[11px] hover:bg-gray-50"
                                >
                                  <input
                                    type="checkbox"
                                    checked={quickProductSupplierIds.includes(id)}
                                    onChange={() => toggleQuickProductSupplier(id)}
                                  />
                                  {labelSupplier(s)}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {quickProductName.trim().length >= 2 && (checkingDuplicates || duplicateSuggestions.length > 0) && (
                      <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-2">
                        {checkingDuplicates ? (
                          <p className="text-[11px] text-amber-700">
                            Vérification des doublons…
                          </p>
                        ) : (
                          <>
                            <p className="text-[11px] font-medium text-amber-800 mb-1.5">
                              Produit(s) similaire(s) déjà existant(s) — utilisez-en un pour éviter un doublon :
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {duplicateSuggestions.map((p: any) => (
                                <button
                                  key={p._id}
                                  type="button"
                                  onClick={() => pickExistingProduct(p)}
                                  title="Ajouter ce produit existant au lieu d'en créer un nouveau"
                                  className="flex items-center gap-1 rounded-md border border-amber-400 bg-white px-2 py-1 text-[11px] hover:bg-amber-100"
                                >
                                  {p.name}
                                  {p.ref && (
                                    <span className="text-amber-700">• {p.ref}</span>
                                  )}
                                  <MdAdd className="w-3 h-3 text-amber-600" />
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <div className="mt-2 flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={resetQuickCreateForm}
                        className="px-3 py-1.5 rounded-lg border text-xs hover:bg-gray-50"
                        disabled={creatingProduct}
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={handleQuickCreateProduct}
                        disabled={!quickProductName.trim() || creatingProduct}
                        className={`px-3 py-1.5 rounded-lg text-xs text-white font-medium ${
                          !quickProductName.trim() || creatingProduct
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-toola-500 hover:bg-toola-600'
                        }`}
                      >
                        {creatingProduct ? 'Création…' : 'Créer et ajouter'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="border rounded-xl p-3 bg-gray-50 max-h-[240px] overflow-y-auto">
                  {loadingProducts ? (
                    <p className="text-xs text-gray-500">Chargement…</p>
                  ) : noSupplier && !search.trim() ? (
                    <p className="text-xs text-gray-500">
                      Recherchez un produit par nom ou référence.
                    </p>
                  ) : filteredProducts.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      Aucun produit trouvé.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {sortedProducts.map((p: any) => {
                        const disabled = alreadyPicked.has(p._id);
                        const alert = isInAlert(p);
                        return (
                          <button
                            key={p._id}
                            disabled={disabled}
                            onClick={() => addLine(p)}
                            className={`text-left rounded-lg border px-3 py-2 text-xs flex justify-between items-center ${
                              disabled
                                ? 'bg-gray-100 border-gray-200 opacity-50'
                                : 'bg-white hover:border-toola-500 hover:bg-toola-50'
                            }`}
                          >
                            <span className="truncate text-[15px]">
                              {p.name || 'Produit'}{' '}
                              {p.ref && (
                                <span className="text-[10px] text-gray-500">
                                  • {p.ref}
                                </span>
                              )}
                              {alert && (
                                  <span className="text-[9px] px-1 py-[1px] ml-2 rounded bg-red-500 text-white">
                                    Alerte
                                  </span>
                               )}
                               <span className="text-[13px] text-gray-800 ml-2">
                                Stock à date: {Number(p.quantity) || 0}
                              </span>
                            </span>
                            {!disabled && (
                              <MdAdd className="w-4 h-4 text-toola-500" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Lignes sélectionnées */}
            {items.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-semibold mb-2">
                  Articles sélectionnés
                </h2>
                <div className="rounded-xl border divide-y">
                  {items.map((l) => (
                    <div
                      key={l.productId}
                      className="flex items-center gap-4 px-4 py-3 bg-white"
                    >
                      <div className="flex-1">
                        <p className="text-xs font-medium text-navy-700 text-[15px]">
                          {l.name}
                        </p>
                        <p className="text-[13px] text-gray-500">
                          Stock actuel: {l.stock} {l.alert > 0 && l.stock <= l.alert && (
                            <span className="ml-1 text-red-500 font-semibold">(!)</span>
                          )}
                        </p>
                        <p className="text-[11px] text-gray-600">
                          Prix unitaire: {formatCurrency(l.unitPrice || 0)}{' '}
                          <span className="text-[10px] italic text-gray-400">
                            ({labelSource(l.unitPriceSource)})
                          </span>
                        </p>
                      </div>
                      <div className="w-28 flex flex-col">
                        <label className="text-[10px] font-medium text-gray-500 mb-1">
                           Quantité
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={l.qty}
                          onChange={(e) =>
                            updateQty(l.productId, Number(e.target.value))
                          }
                          className="w-full rounded-lg border border-gray-300 px-2 py-1 text-xs outline-none focus:border-toola-500"
                        />
                      </div>
                      
                      <button
                        onClick={() => removeLine(l.productId)}
                        className="text-red-500 hover:text-red-600"
                        title="Supprimer"
                      >
                        <MdDelete className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Récapitulatif */}
            {items.length > 0 && (
           <div className="flex justify-end mt-4">
             <div className="text-sm text-navy-700 flex flex-col items-end gap-0.5">
               <div className="flex gap-4">
                 <span className="text-gray-500">Sous-total articles</span>
                 <span className="font-medium">{formatCurrency(totalArticlesHT)}</span>
               </div>
            <div className="flex gap-4">
                 <span className="text-gray-500">Frais de livraison</span>
                 <span className="font-medium">
                   {formatCurrency(deliveryFees || 0)}
                 </span>
            </div>
            <div className="flex gap-4">
                <span className="text-gray-500">TVA totale</span>
                <span className="font-medium">{formatCurrency(totalTVA)}</span>
              </div>
            <div className="flex gap-4 border-t pt-1 mt-1">
                 <span className="font-semibold">Total estimé</span>
                 <span className="font-semibold">
                   {formatCurrency(totalTTC)}
                 </span>
            </div>
            </div>
           </div>
            )}
            {/* Actions */}
            <div className="flex flex-col gap-3 pt-4 border-t">
                <div className="flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 rounded-xl border text-xs hover:bg-gray-50"
                    disabled={submitting}
                  >
                    Annuler
                  </button>
                  <button
                    disabled={!canSubmit || submitting}
                    onClick={() => handleSubmit(false)}
                    className={`px-4 py-2 rounded-xl text-xs text-white font-medium ${
                      !canSubmit || submitting
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-toola-500 hover:bg-toola-600'
                    }`}
                  >
                    {submitting ? 'Création…' : 'Créer'}
                  </button>
                </div>

            </div>
          </Card>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreatePurchaseModal;